import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { BikeleasingContractListItemDTOStatusEnum } from 'probonio-shared-ui/api';
import useDebounce from 'probonio-shared-ui/utils/useDebounce';
import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { useStore } from 'react-redux';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { RootState } from '../../../redux/store';
import {
  ACTIVE_BIKELEASING_CONTRACT_STATUSES,
  ALL_BIKELEASING_CONTRACT_STATUSES,
  INACTIVE_BIKELEASING_CONTRACT_STATUSES,
} from './model/contract-status';

export enum ContractPageEnum {
  Active = 'active',
  Inactive = 'inactive',
  All = 'all',
}

export function getSelectableStatuses(activeTab?: ContractPageEnum): BikeleasingContractListItemDTOStatusEnum[] | undefined {
  if (activeTab === ContractPageEnum.All) {
    return ALL_BIKELEASING_CONTRACT_STATUSES;
  }
  if (activeTab === ContractPageEnum.Inactive) {
    return INACTIVE_BIKELEASING_CONTRACT_STATUSES;
  }
  if (activeTab === ContractPageEnum.Active) {
    return [...ACTIVE_BIKELEASING_CONTRACT_STATUSES, BikeleasingContractListItemDTOStatusEnum.EmployeeApprovalRequired];
  }
  return undefined;
}

export interface ContractFilter {
  searchText?: string;
  selectedStatus?: BikeleasingContractListItemDTOStatusEnum;
  contractPage?: ContractPageEnum;
}

export interface ContractFilterState {
  filter: ContractFilter;
  debouncedFilter: ContractFilter;
  setFilter: Dispatch<SetStateAction<ContractFilter>>;
}
/**
 * instead of using an internal state, connects to the global state in Redux
 */
export function useReduxContractFilterState(
  selector: (state: RootState) => ContractFilter,
  action: ActionCreatorWithPayload<ContractFilter>,
): ContractFilterState {
  const dispatch = useAppDispatch();
  const filter = useAppSelector(selector);
  const { getState } = useStore<RootState>();
  const searchTextDebounced = useDebounce(filter.searchText);

  const setFilter = useCallback<Dispatch<SetStateAction<ContractFilter>>>(
    valueOrSetter => {
      const currentFilter = selector(getState());
      const newValue = typeof valueOrSetter === 'function' ? valueOrSetter(currentFilter) : valueOrSetter;
      dispatch(action(newValue));
    },
    [action, dispatch, getState, selector],
  );

  return useMemo(
    (): ContractFilterState => ({
      filter,
      debouncedFilter: { ...filter, searchText: searchTextDebounced },
      setFilter,
    }),
    [filter, searchTextDebounced, setFilter],
  );
}
